import {Component, ExistingProvider, forwardRef, HostBinding, Input, OnInit, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';
import * as _ from 'lodash-es';
import {map, takeUntil, tap} from 'rxjs/operators';
import {dateFormats} from '../../common/dates';
import {destroy$} from '../destroyable';

export interface DateTimeRange {
    dateFrom: Date;
    dateTo: Date;
}

export type DateTimeFormControls = Record<keyof DateTimeRange, FormControl>;

export interface DateTimeForm extends FormGroup {
    controls: DateTimeFormControls;
}

const DISABLED_CLASS = '-disabled';

const DATE_TIME_RANGE_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
    provide:     NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateTimeRangePickerComponent),
    multi:       true
};

/**
 * Date and time range inputs with dropdown calendars
 * and slider for time range selection.
 *
 * Usage:
 * <date-time-range-picker
 *   [(dateTimeRange)]="dateTimeRange"
 *   (dateTimeRangeChange)="afterDateTimeRangeChanged()">
 * </date-time-range-picker>
 */
@Component({
    selector: 'date-time-range-picker',
    templateUrl: './date-time-range-picker.component.html',
    styleUrls: ['./date-time-range-picker.component.scss'],
    providers: [DATE_TIME_RANGE_CONTROL_VALUE_ACCESSOR],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DateTimeRangePickerComponent implements OnInit, ControlValueAccessor {

    @Input()
    public dateFormat = dateFormats.inputDateTimeFormat;

    @HostBinding(`class.${DISABLED_CLASS}`)
    @HostBinding('attr.disabled')
    public isDisabled: boolean = null; // Null is required in order to remove "disabled" attribute.

    public dateTimeForm: DateTimeForm;

    public readonly destroy$ = destroy$();

    public ngOnInit(): void {
        this.buildForm();
    }

    public propagateChange(timestampRange: [number, number]): void {
        // To be overridden by parent via registerOnChange.
    }

    public registerOnChange(handler: (timestampRange: [number, number]) => void): void {
        this.propagateChange = handler;
    }

    public registerOnTouched(handler: () => void): void {
        // Not implemented.
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled || null; // Null is required in order to remove "disabled" attribute.
    }

    public writeValue(timestampRange: [number, number]): void {
        const [from, to] = timestampRange || [null, null];
        const formValue: DateTimeRange = {
            dateFrom: !_.isNil(from) ? new Date(from) : null,
            dateTo: !_.isNil(to) ? new Date(to) : null
        };

        this.dateTimeForm.reset(formValue, {emitEvent: false});
    }

    private buildForm(): void {
        const controls: DateTimeFormControls = {
            dateTo: new FormControl(null),
            dateFrom: new FormControl(null)
        };

        this.dateTimeForm = new FormGroup(controls) as DateTimeForm;

        this.listenToDateTimeRangeChange();
    }

    private listenToDateTimeRangeChange(): void {
        const getChangedDate = (date: Date) => !_.isNil(date)
            ? date.valueOf()
            : null;

        this.dateTimeForm.valueChanges.pipe(
            map<DateTimeRange, [number, number]>(({dateFrom, dateTo}) => [
                getChangedDate(dateFrom),
                getChangedDate(dateTo)
            ]),
            tap(timestampRange => this.propagateChange(timestampRange)),
            takeUntil(this.destroy$)
        ).subscribe();
    }
}
